// RegionBadge — header chip showing the dispatcher's current region scope.
// For super-admin: dropdown allowing selection of ALL / DMV / NJ.
// For regional dispatchers: read-only badge showing their locked region.
//
// Hidden entirely for customers and drivers (region is administrative
// metadata, not user-facing).

import { Globe, Map as MapIcon } from 'lucide-react';
import { useRegionScope } from '../context/RegionContext';
import { useAuth } from '../context/AuthContext';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from './ui/select';

export function RegionBadge() {
  const { user } = useAuth();
  const { selectedRegion, setSelectedRegion, regions, isSuperAdmin } = useRegionScope();

  // Only operators see this. Customers + drivers should never see region.
  if (!user || !['dispatcher', 'super_admin'].includes(user.role)) return null;

  const currentLabel = (() => {
    if (selectedRegion === 'all') return 'ALL';
    const r = regions.find((x) => x.code === selectedRegion);
    return r?.name?.toUpperCase() || selectedRegion?.toUpperCase() || '?';
  })();

  if (!isSuperAdmin) {
    // Regional dispatcher — read-only chip
    return (
      <div
        className="flex items-center gap-2 px-3 py-1.5 border border-[#2A0F3D] bg-[rgba(57,255,20,0.04)] font-mono text-xs uppercase text-[#39FF14] rounded-none"
        data-testid="region-badge"
        title="You are scoped to this region"
      >
        <MapIcon className="w-3 h-3" />
        DISPATCH · {currentLabel}
      </div>
    );
  }

  // Super-admin — dropdown picker
  return (
    <Select
      value={selectedRegion || 'all'}
      onValueChange={(v) => setSelectedRegion(v)}
    >
      <SelectTrigger
        className="w-[180px] h-9 border-[#00F0FF] text-[#00F0FF] hover:bg-[rgba(0,240,255,0.08)] rounded-none font-mono text-xs uppercase bg-[#0C0612]"
        data-testid="region-switcher"
      >
        <div className="flex items-center gap-2">
          <Globe className="w-3 h-3" />
          <SelectValue />
        </div>
      </SelectTrigger>
      <SelectContent className="bg-[#0C0612] border-[#00F0FF] text-[#39FF14] font-mono">
        <SelectItem value="all" className="cursor-pointer" data-testid="region-option-all">
          ALL REGIONS
        </SelectItem>
        {regions.map((r) => (
          <SelectItem
            key={r.code}
            value={r.code}
            className="cursor-pointer"
            data-testid={`region-option-${r.code}`}
          >
            {r.name?.toUpperCase() || r.code.toUpperCase()}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}
